// Application state.
//
// One object, because six screens do not need a state library. The rule the
// whole file is arranged around: a tap that logs a set updates this state and
// returns. Persisting and syncing happen after, and their failure shows up as
// a banner, never as a blocked button.

import { api, loadToken, saveToken, OfflineError } from './api';
import { byIndex, clear, getAll, metaGet, metaSet, putAll, STORE } from './idb';
import {
  commitFinish,
  commitSession,
  commitSet,
  deadLetters,
  depth,
  flush,
  newId,
  requestBackgroundSync,
  saveLocalSession,
} from './outbox';
import { keepScreenAwake, releaseWakeLock } from './wakelock';
import type {
  Bootstrap,
  Effort,
  Exercise,
  LocalSession,
  LocalSet,
  PlanDay,
  QueuedSet,
  SessionMode,
  SessionOverview,
  SkipReason,
} from './types';

type Screen = 'loading' | 'pair' | 'home' | 'session' | 'finish' | 'history';

/** Patterns that count as the compound work a fifteen-minute session keeps. */
const COMPOUND = new Set([
  'squat',
  'hinge',
  'lunge',
  'horizontal_push',
  'horizontal_pull',
  'vertical_push',
  'vertical_pull',
]);

const ACTIVE_QUEUE_KEY = 'active_queue';
const BOOTSTRAP_KEY = 'bootstrap';

function buildQueue(
  day: PlanDay,
  exercises: Map<string, Exercise>,
  last: Bootstrap['last_by_exercise'],
  mode: SessionMode,
): QueuedSet[] {
  let items = day.items.slice().sort((a, b) => a.position - b.position);

  if (mode === 'short') {
    // Low energy: three compound sets, about fifteen minutes, logged as a real
    // session. Keeps a skipped week from becoming a skipped month.
    //
    // "Compound" here means a compound pattern AND loadable: a band face pull
    // is a horizontal pull, but it is four minutes of posture work, not one of
    // the three things worth keeping when he has fifteen minutes.
    const isCompound = (exerciseId: string) => {
      const ex = exercises.get(exerciseId);
      return !!ex && COMPOUND.has(ex.pattern ?? '') && ex.load_step_kg != null;
    };
    const compound = items.filter((i) => isCompound(i.exercise_id));
    const chosen = [...compound, ...items.filter((i) => !isCompound(i.exercise_id))].slice(0, 3);
    items = chosen
      .sort((a, b) => a.position - b.position)
      .map((i) => ({ ...i, target_sets: Math.min(i.target_sets, 3) }));
  }

  // set_index is "the Nth set of this exercise in this session", which is what
  // the database's unique key means. If a day ever lists the same exercise
  // twice, the second block has to carry on numbering rather than start at 1,
  // or the two collide on the way to the server.
  const totalByExercise = new Map<string, number>();
  for (const item of items) {
    totalByExercise.set(
      item.exercise_id,
      (totalByExercise.get(item.exercise_id) ?? 0) + item.target_sets,
    );
  }
  const doneByExercise = new Map<string, number>();

  const out: QueuedSet[] = [];
  items.forEach((item, index) => {
    const ex = exercises.get(item.exercise_id);
    if (!ex) return;
    for (let n = 1; n <= item.target_sets; n++) {
      const setIndex = (doneByExercise.get(ex.id) ?? 0) + 1;
      doneByExercise.set(ex.id, setIndex);
      out.push({
        plan_item_id: item.id,
        exercise_id: ex.id,
        exercise_name: ex.name,
        ui_type: ex.ui_type,
        load_unit: ex.load_unit,
        load_step_kg: ex.load_step_kg,
        load_min_kg: ex.load_min_kg,
        load_max_kg: ex.load_max_kg,
        set_index: setIndex,
        set_total: totalByExercise.get(ex.id) ?? item.target_sets,
        exercise_position: index + 1,
        exercise_count: items.length,
        target_reps: item.target_reps_min,
        // the plan's target wins; otherwise fall back to what he last lifted
        target_weight_kg: item.target_weight_kg ?? last[ex.id]?.weight_kg ?? null,
        target_time_seconds: item.target_time_seconds,
        per_side: item.per_side,
        rest_seconds: item.rest_seconds ?? ex.default_rest_seconds,
        cue: item.cue ?? ex.cue,
      });
    }
  });
  return out;
}

class App {
  screen = $state<Screen>('loading');
  online = $state(typeof navigator === 'undefined' ? true : navigator.onLine);

  bootstrap = $state<Bootstrap | null>(null);
  history = $state<SessionOverview[]>([]);
  historyLoading = $state(false);

  /** queue depth; shown so he can see work is waiting, not lost */
  pending = $state(0);
  syncNote = $state<string | null>(null);
  /** writes the server refused outright - they are kept, not dropped */
  rejected = $state(0);
  saveError = $state<string | null>(null);

  session = $state<LocalSession | null>(null);
  queue = $state<QueuedSet[]>([]);
  logged = $state<LocalSet[]>([]);

  /** a resumable session found on startup */
  resumable = $state<LocalSession | null>(null);

  /** epoch ms the rest timer ends, or null when not resting */
  restEndsAt = $state<number | null>(null);
  /** epoch ms a count-up (plank) timer started, or null */
  timerStartedAt = $state<number | null>(null);
  now = $state(Date.now());

  updateReady = $state(false);
  #waitingWorker: ServiceWorker | null = null;

  #tick: ReturnType<typeof setInterval> | null = null;
  #buzzed = false;

  exerciseById = $derived(new Map((this.bootstrap?.exercises ?? []).map((e) => [e.id, e])));

  current = $derived<QueuedSet | null>(
    this.session && this.session.cursor < this.queue.length
      ? this.queue[this.session.cursor]
      : null,
  );

  restRemaining = $derived(
    this.restEndsAt === null ? 0 : Math.max(0, (this.restEndsAt - this.now) / 1000),
  );

  elapsed = $derived(
    this.timerStartedAt === null ? 0 : Math.max(0, (this.now - this.timerStartedAt) / 1000),
  );

  // ---------------------------------------------------------------- startup

  async init(): Promise<void> {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.online = true;
        void this.sync();
      });
      window.addEventListener('offline', () => {
        this.online = false;
      });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.now = Date.now();
          void this.sync();
        }
      });
    }

    const token = await loadToken();
    this.pending = await depth();
    this.rejected = (await deadLetters()).length;

    // Cached bootstrap first, so the app is usable before any network call.
    this.bootstrap = (await metaGet<Bootstrap>(BOOTSTRAP_KEY)) ?? null;
    this.history = (await getAll<SessionOverview>(STORE.history)).sort((a, b) =>
      b.started_at.localeCompare(a.started_at),
    );

    if (!token) {
      this.screen = 'pair';
      return;
    }

    await this.resumeIfAny();
    if (this.screen === 'loading') this.screen = 'home';

    void this.refresh();
    void this.sync();
  }

  async pair(token: string): Promise<void> {
    await saveToken(token);
    this.screen = 'home';
    await this.refresh();
  }

  /** Pull the plan, library and history. Failure is fine - the cache stands. */
  async refresh(): Promise<void> {
    try {
      const boot = await api.get<Bootstrap>('/api/bootstrap');
      this.bootstrap = boot;
      await metaSet(BOOTSTRAP_KEY, boot);
    } catch (err) {
      if (!(err instanceof OfflineError)) this.syncNote = 'could not refresh the plan';
    }
    await this.loadHistory();
  }

  async loadHistory(): Promise<void> {
    this.historyLoading = true;
    try {
      const res = await api.get<{ sessions: SessionOverview[] }>('/api/sessions?limit=200');
      this.history = res.sessions;
      // Replace the cache rather than merging into it. Merging only ever adds,
      // so a session deleted on the server would keep reappearing in the
      // offline list - a row the device believes in and nothing else does.
      await clear(STORE.history);
      await putAll(STORE.history, res.sessions);
    } catch {
      // offline: the cached list is already on screen
    } finally {
      this.historyLoading = false;
    }
  }

  async sync(): Promise<void> {
    if (!this.online) return;
    const outcome = await flush();
    this.pending = outcome.remaining;
    if (outcome.dropped > 0) this.rejected = (await deadLetters()).length;
    if (outcome.error === 'unauthorised') {
      this.syncNote = 'the API token was rejected';
    } else if (outcome.error && outcome.remaining > 0) {
      this.syncNote = `${outcome.remaining} to sync`;
      void requestBackgroundSync();
    } else {
      this.syncNote = null;
      if (outcome.sent > 0) void this.loadHistory();
    }
  }

  // --------------------------------------------------------------- sessions

  async resumeIfAny(): Promise<void> {
    const sessions = await getAll<LocalSession>(STORE.sessions);
    const open = sessions
      .filter((s) => s.finished_at === null)
      .sort((a, b) => b.started_at.localeCompare(a.started_at))[0];
    if (open) this.resumable = open;
  }

  async resume(session: LocalSession): Promise<void> {
    this.session = session;
    this.queue = (await metaGet<QueuedSet[]>(ACTIVE_QUEUE_KEY)) ?? [];
    this.logged = (await byIndex<LocalSet>(STORE.sets, 'by_session', session.id)).sort(
      (a, b) => a.order_index - b.order_index,
    );
    this.resumable = null;
    this.screen = 'session';
    this.startClock();
    void keepScreenAwake();
  }

  /** Close an abandoned session instead of deleting it: a ghost is the signal. */
  async discardResumable(): Promise<void> {
    const open = this.resumable;
    if (!open) return;
    this.resumable = null;
    const finished: LocalSession = {
      ...open,
      finished_at: new Date().toISOString(),
      duration_seconds: Math.max(
        0,
        Math.round((Date.now() - new Date(open.started_at).getTime()) / 1000),
      ),
    };
    await commitFinish(finished);
    this.pending = await depth();
    void this.sync();
  }

  async start(day: PlanDay, mode: SessionMode = 'full'): Promise<void> {
    const started = new Date().toISOString();
    const session: LocalSession = {
      id: newId(),
      plan_day_id: day.id,
      day_name: day.name,
      mode,
      started_at: started,
      finished_at: null,
      duration_seconds: null,
      energy_before: mode === 'short' ? 2 : null,
      cursor: 0,
    };
    const queue = buildQueue(day, this.exerciseById, this.bootstrap?.last_by_exercise ?? {}, mode);

    this.session = session;
    this.queue = queue;
    this.logged = [];
    this.restEndsAt = null;
    this.timerStartedAt = null;
    this.screen = 'session';
    this.startClock();
    void keepScreenAwake();

    await metaSet(ACTIVE_QUEUE_KEY, queue);
    await commitSession(session);
    this.pending = await depth();
    void this.sync();
  }

  /**
   * Log the set that is on screen.
   *
   * State changes first and the screen moves on; the write to IndexedDB and the
   * sync are started but not waited for.
   */
  log(effort: Effort, actual: { reps?: number | null; weight?: number | null; time?: number | null }): void {
    const q = this.current;
    const session = this.session;
    if (!q || !session) return;

    const set: LocalSet = {
      id: newId(),
      session_id: session.id,
      exercise_id: q.exercise_id,
      plan_item_id: q.plan_item_id,
      set_index: q.set_index,
      order_index: this.logged.length + 1,
      weight_kg: actual.weight ?? null,
      reps: actual.reps ?? null,
      time_seconds: actual.time ?? null,
      effort,
      completed: true,
      skip_reason: null,
      performed_at: new Date().toISOString(),
    };

    this.record(set, q.rest_seconds);
  }

  /** A set he did not do. The reason is the point: it turns a null into signal. */
  skip(reason: SkipReason): void {
    const q = this.current;
    const session = this.session;
    if (!q || !session) return;

    const set: LocalSet = {
      id: newId(),
      session_id: session.id,
      exercise_id: q.exercise_id,
      plan_item_id: q.plan_item_id,
      set_index: q.set_index,
      order_index: this.logged.length + 1,
      weight_kg: null,
      reps: null,
      time_seconds: null,
      effort: null,
      completed: false,
      skip_reason: reason,
      performed_at: new Date().toISOString(),
    };

    this.record(set, 0);
  }

  private record(set: LocalSet, restSeconds: number): void {
    const session = this.session;
    if (!session) return;

    this.logged = [...this.logged, set];
    session.cursor += 1;
    this.timerStartedAt = null;
    this.restEndsAt = restSeconds > 0 ? Date.now() + restSeconds * 1000 : null;
    this.#buzzed = false;
    this.now = Date.now();

    if (session.cursor >= this.queue.length) {
      this.restEndsAt = null;
      this.screen = 'finish';
      this.stopClock();
    }

    // Everything above was synchronous. Only now does anything leave the tap.
    void (async () => {
      try {
        await commitSet(set);
        await saveLocalSession(session);
        this.saveError = null;
        this.pending = await depth();
      } catch {
        this.saveError = 'this set is not saved on the device - screenshot it';
        return;
      }
      void this.sync();
    })();
  }

  startTimer(): void {
    this.timerStartedAt = Date.now();
    this.now = Date.now();
  }

  skipRest(): void {
    this.restEndsAt = null;
  }

  addRest(seconds: number): void {
    this.restEndsAt = Math.max(Date.now(), this.restEndsAt ?? Date.now()) + seconds * 1000;
  }

  async finish(): Promise<void> {
    const session = this.session;
    if (!session) return;
    const finished: LocalSession = {
      ...session,
      finished_at: new Date().toISOString(),
      duration_seconds: Math.max(
        0,
        Math.round((Date.now() - new Date(session.started_at).getTime()) / 1000),
      ),
    };
    this.session = finished;
    this.stopClock();
    void releaseWakeLock();

    await commitFinish(finished);
    this.pending = await depth();
    void this.sync();
  }

  async leaveSession(): Promise<void> {
    this.session = null;
    this.queue = [];
    this.logged = [];
    this.restEndsAt = null;
    this.screen = 'home';
    this.stopClock();
    void releaseWakeLock();
    await this.resumeIfAny();
  }

  /** End a session early from the session screen. */
  endEarly(): void {
    this.restEndsAt = null;
    this.screen = 'finish';
    this.stopClock();
  }

  go(screen: Screen): void {
    this.screen = screen;
    if (screen === 'history') void this.loadHistory();
  }

  // ----------------------------------------------------------------- updates

  offerUpdate(worker: ServiceWorker): void {
    this.#waitingWorker = worker;
    this.updateReady = true;
  }

  /**
   * Apply a waiting update.
   *
   * Reloading on its own does nothing: a waiting worker only takes over once
   * every page the old one controls has gone, and a reload keeps this client
   * alive. So the waiting worker has to be told to skip waiting, and the reload
   * happens when it actually takes control (see main.ts).
   */
  applyUpdate(): void {
    const worker = this.#waitingWorker;
    if (!worker) {
      location.reload();
      return;
    }
    this.updateReady = false;
    worker.postMessage('skip-waiting');

    // If the worker never takes over - it was discarded, or the browser is
    // being awkward - do not leave him staring at a button that did nothing.
    setTimeout(() => location.reload(), 3000);
  }

  // ------------------------------------------------------------------ clock

  private startClock(): void {
    if (this.#tick) return;
    this.#tick = setInterval(() => {
      this.now = Date.now();
      // A short buzz when the rest is up. No notification: this is phase 1,
      // and a timer that has to survive a locked screen is phase 6's job.
      if (this.restEndsAt !== null && this.now >= this.restEndsAt && !this.#buzzed) {
        this.#buzzed = true;
        this.restEndsAt = null;
        navigator.vibrate?.([120, 80, 120]);
      }
    }, 250);
  }

  private stopClock(): void {
    if (this.#tick) clearInterval(this.#tick);
    this.#tick = null;
  }
}

export const app = new App();

/** The day to offer on the home screen, and the last session he did. */
export function chooseNextDay(
  plan: { days: PlanDay[] } | null,
  history: SessionOverview[],
): { day: PlanDay | null; last: SessionOverview | null } {
  if (!plan || plan.days.length === 0) return { day: null, last: null };

  const real = history
    .filter((s) => s.completed_set_count > 0)
    .sort((a, b) => b.started_at.localeCompare(a.started_at));
  const last = real[0] ?? null;

  // Phase 1 rule: the day after the last one he actually trained, cycling
  // through the plan. "What is undertrained" is phase 2's job.
  const days = plan.days.slice().sort((a, b) => a.day_index - b.day_index);
  const lastIndex = last?.plan_day_id ? days.findIndex((d) => d.id === last.plan_day_id) : -1;
  const next = days[(lastIndex + 1) % days.length];
  return { day: next ?? days[0], last };
}
